/**
 * Indigo Navbar component for the design system.
 * Renders a collapsible sidebar navigation with tabs, logo, and profile section.
 *
 * Features:
 * - Collapsed/expanded state on hover and pin
 * - Customizable tab list
 * - Profile block at the bottom
 * - Pin button to fix expanded state
 *
 * Usage:
 *   <Navbar tabs={tabs} profileImageUrl={url} profileName="User" profileUrl="/profile" />
 */

import IndigoLogoSymbol from "@assets/logos/indigo_symbol.svg?react";
import IndigoLogoSymbolDev from "@assets/logos/indigo_symbol-dev.svg?react";
import { Dropdown, type DropdownProps, Popover, Tooltip } from "antd";
import { clsx } from "clsx";
import {
  forwardRef,
  HTMLAttributes,
  ReactNode,
  useEffect,
  useMemo,
  useState,
} from "react";
import { matchPath, useLocation, useNavigate } from "react-router-dom";
import { getCurrentEnv } from "~/common/utils";
import { Avatar } from "../Avatar";
import { Icon, type IconProps } from "../Icons/Icons";
import "./Navbar.css";
import { NavbarTab } from "./NavbarTab";

// Profile menu item types
export type NavbarProfileMenuItem =
  | {
      type?: "item";
      key: string;
      label: string;
      icon?: ReactNode;
      onClick: () => void;
    }
  | { type: "divider" };

// Type narrowing to include separator and heading tabs
interface SeparatorTab {
  type: "separator";
}

interface HeadingTab {
  type: "heading";
  title: string;
  collapsedTitle?: string;
}

export type NavbarTabType =
  | {
      /** Stable identifier for parent-child relationships and advanced active-state handling. */
      id?: string;
      /** Parent tab id. When present, this tab is considered a child item. */
      parentId?: string;
      to?: string;
      title: string;
      iconName?: IconProps["name"];
      /** Render only when navbar is expanded (hover/pinned/forced). */
      expandedOnly?: boolean;
      type?: "separator";
      external?: boolean;
      tag?: ReactNode;
      /** Visual override for title text. When provided, renders instead of `title` (title still used for aria-label). */
      titleNode?: ReactNode;
      /** Click handler — when provided (without `to`), renders a button instead of a NavLink */
      onClick?: () => void;
      /** Force active styling (useful for onClick tabs) */
      isActive?: boolean;
      /** Internal active override resolved by Navbar for parent/child route handling. */
      forceIsActive?: boolean;
      /** Indicates the tab has expandable child entries. */
      hasExpandableChildren?: boolean;
      /** Current navbar expansion state for accessibility and focus management. */
      isNavbarExpanded?: boolean;
      /** Show a small dot on the icon when navbar is collapsed (e.g. unread notifications). */
      showCollapsedDot?: boolean;
    }
  | SeparatorTab
  | HeadingTab;

export interface NavbarAdminSection {
  title: string;
  iconName: IconProps["name"];
  items: {
    to: string;
    title: string;
    iconName: IconProps["name"];
  }[];
}

export interface NavbarProps extends HTMLAttributes<HTMLElement> {
  /**
   * Array of tab objects for navigation. Each tab can be a link or separator.
   */
  tabs: NavbarTabType[];
  /**
   * Optional CSS class for custom styling.
   */
  className?: string;
  /**
   * Profile image URL for the user block at the bottom.
   */
  profileImageUrl?: string;
  /**
   * Profile name to display next to the image.
   */
  profileName?: string;
  /**
   * Internal link for the profile block.
   */
  profileUrl?: string;
  /**
   * Initial pinned state.
   */
  initialPinned?: boolean;
  /**
   * Controlled pinned state from parent (overrides internal state).
   */
  pinned?: boolean;
  /**
   * Whether pin button is allowed to be clicked (false disables the button).
   */
  isPinAllowed?: boolean;
  /**
   * Callback fired when pin state changes (expanded/collapsed fixed).
   */
  onPinChange?: (isPinned: boolean) => void;
  /**
   * Array of menu items for the profile dropdown.
   */
  profileMenuItems?: NavbarProfileMenuItem[];
  /**
   * Admin section with flyout submenu, rendered above the profile block.
   */
  adminSection?: NavbarAdminSection;
  /**
   * Optional warning surfaced on the profile avatar — renders a small dot and
   * shows `tooltip` on hover. Used to flag a soon-to-expire session.
   */
  profileWarning?: {
    tooltip: ReactNode;
  };
  /**
   * Render as the contents of an off-canvas panel rather than a fixed rail.
   *
   * The rail's whole interaction model — a 64px strip that widens on hover,
   * pinned open or not — assumes a pointer and spare horizontal room. A phone
   * has neither, so below the layout breakpoint the host puts the navbar in a
   * sheet instead. In that mode it fills its container, is always expanded,
   * paints no surface of its own (the sheet does), and drops the pin control,
   * which would toggle a width nothing reads.
   *
   * @default false
   */
  isOffCanvas?: boolean;
  /**
   * Called after a tab is activated. The host uses it to dismiss the
   * containing sheet — an off-canvas panel that stays open over the page it
   * just navigated to is a dead end on a small screen.
   */
  onNavigate?: () => void;
}

export const Navbar = forwardRef<HTMLElement, NavbarProps>(
  // State: pinned (fixed expanded), hovered (temporary expanded)
  (
    {
      tabs,
      className,
      profileImageUrl,
      profileName = "Profile",
      profileUrl,
      initialPinned = false,
      pinned: controlledPinned,
      isPinAllowed = true,
      onPinChange,
      profileMenuItems = [],
      adminSection,
      profileWarning,
      isOffCanvas = false,
      onNavigate,
      ...props
    },
    ref,
  ) => {
    const [internalPinned, setInternalPinned] = useState(initialPinned);
    const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
    const [isAdminMenuOpen, setIsAdminMenuOpen] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const isProd = getCurrentEnv() === "prod";
    const navigate = useNavigate();
    const { pathname } = useLocation();

    // Ensure all menu overlays are closed after navigation.
    // biome-ignore lint/correctness/useExhaustiveDependencies: pre-existing; deps reviewed, auto-fix risks behavior change
    useEffect(() => {
      setIsAdminMenuOpen(false);
      setIsUserMenuOpen(false);
    }, [pathname]);

    // Use controlled pinned state if provided, otherwise use internal state
    const pinned =
      controlledPinned !== undefined ? controlledPinned : internalPinned;

    /**
     * Handles pin button click: toggles pinned state and fires callback.
     */
    const handlePinClick = () => {
      // Don't allow pinning when pin is disabled
      if (!isPinAllowed) return;

      const newPinnedState = !pinned;

      // Update internal state if not controlled
      if (controlledPinned === undefined) {
        setInternalPinned(newPinnedState);
      }

      // Always notify parent of the change
      if (onPinChange) onPinChange(newPinnedState);
    };

    const handleUserMenuOpenChange: DropdownProps["onOpenChange"] = (
      nextOpen,
      info,
    ) => {
      if (info.source === "trigger") {
        setIsUserMenuOpen(nextOpen);
      }
    };

    const handleCloseMenuOnMouseLeave = () => {
      setIsHovered(false);
      if (isUserMenuOpen && !pinned) {
        setIsUserMenuOpen(false);
      }
    };

    // Off-canvas has no collapsed state to expand from — it is opened
    // deliberately and fills the sheet, so every label is shown.
    const isNavbarExpanded =
      isOffCanvas || pinned || isAdminMenuOpen || isHovered;

    const resolvedTabs: NavbarTabType[] = useMemo(() => {
      const normalized = tabs.map((tab, idx) => ({ tab, idx }));

      const baseActiveByIndex = new Map<number, boolean>();
      const childActiveByParent = new Map<string, boolean>();
      const hasChildrenByParent = new Map<string, boolean>();

      normalized.forEach(({ tab, idx }) => {
        if (tab.type === "separator" || tab.type === "heading") {
          return;
        }

        if (tab.parentId) {
          hasChildrenByParent.set(tab.parentId, true);
        }

        // Keep active-state matching aligned with <NavLink end> behavior in NavbarTab.
        const routeActive =
          !!tab.to && !!matchPath({ path: tab.to, end: true }, pathname);
        const baseActive = tab.isActive ?? routeActive;

        baseActiveByIndex.set(idx, baseActive);

        if (tab.parentId && baseActive) {
          childActiveByParent.set(tab.parentId, true);
        }
      });

      return normalized.map(({ tab, idx }) => {
        if (tab.type === "separator" || tab.type === "heading") {
          return tab;
        }

        const baseActive = baseActiveByIndex.get(idx) ?? false;
        const hasActiveChild = !!tab.id && childActiveByParent.get(tab.id);
        const hasExpandableChildren =
          !!tab.id && hasChildrenByParent.get(tab.id);
        const forceIsActive = hasActiveChild ? !isNavbarExpanded : baseActive;

        return {
          ...tab,
          forceIsActive,
          hasExpandableChildren,
          isNavbarExpanded,
        };
      });
    }, [tabs, pathname, isNavbarExpanded]);

    // Convert profileMenuItems to Ant Design menu items format
    const menuItems = profileMenuItems.map((item, index) => {
      if (item.type === "divider") {
        return { type: "divider" as const, key: `divider-${index}` };
      }
      return {
        key: item.key,
        icon: item.icon,
        label: item.label,
        onClick: () => {
          item.onClick();
          setIsUserMenuOpen(false);
        },
      };
    });

    return (
      <nav
        {...props}
        ref={ref}
        className={clsx(
          "indigo-navbar",
          pinned && "is-pinned",
          // `is-expanded` is what the CSS keys the `.collapsible` label
          // reveal off — `isNavbarExpanded` above is only used for props.
          // Off-canvas has no collapsed state, so the labels are never
          // conditional there; without this the panel was a column of
          // unlabelled icons that only named themselves on hover.
          (isAdminMenuOpen || isOffCanvas) && "is-expanded",
          isOffCanvas && "is-off-canvas",
          className,
        )}
        // Hover expansion is meaningless off-canvas (already expanded) and
        // misleading on touch, where the "hover" is a stuck first tap.
        onMouseEnter={isOffCanvas ? undefined : () => setIsHovered(true)}
        onMouseLeave={isOffCanvas ? undefined : handleCloseMenuOnMouseLeave}
        onClickCapture={
          onNavigate
            ? (event) => {
                // Dismiss on any activation inside the tab list. Capture
                // phase, so it runs whatever the tab does with the event —
                // but only for tabs: the admin flyout and the profile menu
                // open sub-UI inside the panel and must not close it.
                if (
                  event.target instanceof Element &&
                  event.target.closest(
                    ".indigo-navbar-tab-list a," +
                      " .indigo-navbar-tab-list button",
                  )
                ) {
                  onNavigate();
                }
              }
            : undefined
        }
      >
        {/* Header: logo and pin button */}
        <div className="indigo-navbar__header">
          {!isProd ? (
            <IndigoLogoSymbolDev
              width="32"
              height="32"
              className={"indigo-navbar-logo min-w-[32px] min-h-[32px]"}
            />
          ) : (
            <IndigoLogoSymbol
              width="32"
              height="32"
              className={"indigo-navbar-logo p-[4px] min-w-[32px] min-h-[32px]"}
            />
          )}
          {!isOffCanvas && (
            <button
              onClick={handlePinClick}
              className={clsx(
                "indigo-navbar-pin-btn mt-[4px] collapsible",
                !isPinAllowed && "opacity-40 cursor-not-allowed",
              )}
              aria-label={pinned ? "Unpin navbar" : "Pin navbar"}
              disabled={!isPinAllowed}
            >
              <Icon
                className="indigo-navbar-icon"
                name="left-sidebar-icon"
                size="m"
              />
            </button>
          )}
        </div>

        {/* Body: tab list */}
        <div className="indigo-navbar__body">
          <ul className={"indigo-navbar-tab-list"}>
            {resolvedTabs.map((tab, idx) => (
              <NavbarTab
                key={
                  tab.type === "separator" || tab.type === "heading"
                    ? `${tab.type}-${idx}`
                    : (tab.id ?? `${tab.to ?? tab.title}-${idx}`)
                }
                {...tab}
              />
            ))}
          </ul>
        </div>

        {/* Footer: admin section + user avatar */}
        <div className={clsx("indigo-navbar__footer")}>
          {adminSection &&
            adminSection.items.length > 0 &&
            (isOffCanvas ? (
              /*
               * Off-canvas: the submenu opens INLINE, below its trigger.
               *
               * The flyout below is a `rightBottom` popover anchored to a
               * 200px rail with the whole viewport to its right. In a sheet
               * that occupies most of the screen there is no "right" left —
               * the menu landed off-screen and dragged a horizontal
               * scrollbar onto the page. An accordion is also the honest
               * touch affordance: `trigger="hover"` has no meaning here, and
               * antd resolves it to a tap that then cannot be tapped away.
               */
              <div className="indigo-navbar-admin-inline">
                <button
                  type="button"
                  className="indigo-navbar-admin-trigger"
                  aria-expanded={isAdminMenuOpen}
                  onClick={() => setIsAdminMenuOpen((open) => !open)}
                >
                  <Icon
                    className="indigo-navbar-icon"
                    name={adminSection.iconName}
                    size="m"
                  />
                  <span className="indigo-navbar-admin-text collapsible truncate">
                    {adminSection.title}
                  </span>
                  <Icon
                    className={clsx(
                      "indigo-navbar-admin-chevron collapsible",
                      isAdminMenuOpen && "is-open",
                    )}
                    name="chevron-right"
                    size="s"
                  />
                </button>
                {isAdminMenuOpen && (
                  <div className="indigo-navbar-admin-menu-content indigo-navbar-admin-menu-content--inline">
                    {adminSection.items.map((item) => (
                      <button
                        type="button"
                        key={item.to}
                        className="indigo-navbar-admin-menu-item"
                        onClick={() => {
                          navigate(item.to);
                          setIsAdminMenuOpen(false);
                          // These live in the footer, outside the tab list
                          // the sheet's dismiss handler watches — but they
                          // navigate just the same, so they dismiss too.
                          onNavigate?.();
                        }}
                      >
                        <Icon
                          className="indigo-navbar-icon"
                          name={item.iconName}
                          size="s"
                        />
                        <span>{item.title}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <Popover
                open={isAdminMenuOpen}
                content={
                  <div className="indigo-navbar-admin-menu-content">
                    {adminSection.items.map((item) => (
                      <button
                        type="button"
                        key={item.to}
                        className="indigo-navbar-admin-menu-item"
                        onClick={() => {
                          navigate(item.to);
                          setIsAdminMenuOpen(false);
                        }}
                      >
                        <Icon
                          className="indigo-navbar-icon"
                          name={item.iconName}
                          size="s"
                        />
                        <span>{item.title}</span>
                      </button>
                    ))}
                  </div>
                }
                trigger="hover"
                placement="rightBottom"
                overlayClassName="indigo-navbar-admin-menu"
                onOpenChange={setIsAdminMenuOpen}
                arrow={false}
              >
                <div className="indigo-navbar-admin-trigger">
                  <Icon
                    className="indigo-navbar-icon"
                    name={adminSection.iconName}
                    size="m"
                  />
                  <span className="indigo-navbar-admin-text collapsible truncate">
                    {adminSection.title}
                  </span>
                  <Icon
                    className="indigo-navbar-admin-chevron collapsible"
                    name="chevron-right"
                    size="s"
                  />
                </div>
              </Popover>
            ))}
          <Dropdown
            menu={{ items: menuItems }}
            onOpenChange={handleUserMenuOpenChange}
            open={isUserMenuOpen}
            trigger={["click"]}
            overlayClassName="indigo-navbar-profile-menu"
          >
            <div className="indigo-navbar-profile">
              {profileWarning ? (
                <Tooltip title={profileWarning.tooltip} placement="right">
                  <span className="indigo-navbar-profile__avatar-warning">
                    <Avatar
                      className="flex-shrink-0"
                      src={profileImageUrl}
                      size="sm"
                      name={profileName}
                    />
                  </span>
                </Tooltip>
              ) : (
                <Avatar
                  className="flex-shrink-0"
                  src={profileImageUrl}
                  size="sm"
                  name={profileName}
                />
              )}
              <span className="indigo-navbar-profile__name collapsible truncate">
                {profileName}
              </span>
            </div>
          </Dropdown>
        </div>
      </nav>
    );
  },
);

Navbar.displayName = "Navbar";

export default Navbar;
